import { getAnalyticsId } from '@shared/core/analytics/segment/getAnalyticsId';
import { getSessionId } from '@shared/core/analytics/segment/getTrackerIds';
import { withWindow } from '@shared/utils/withWindow';
import { addError } from '@shared/utils/logger';

type ClarityApi = Readonly<{
  (command: 'identify', userId: string, sessionId?: string, pageId?: string, friendlyName?: string): void;
  (command: 'set', key: string, value: string | ReadonlyArray<string>): void;
  (command: 'upgrade', reason: string): void;
}>;

declare global {
  interface Window {
    readonly clarity?: ClarityApi;
  }
}

let hasIdentifiedWithClarity = false;

/**
 * Identifies the current user with Microsoft Clarity by sending our analytics ID.
 * This creates a bidirectional link between Clarity sessions and our analytics.
 *
 * Safe to call before Clarity SDK loads - commands are queued and replayed.
 *
 * @see https://learn.microsoft.com/en-us/clarity/setup-and-installation/identify-api
 */
export const identifyWithClarity = (): void => {
  const analyticsId = getAnalyticsId();
  const sessionId = getSessionId();

  withWindow(globalWindow => {
    const clarity = globalWindow.clarity;

    if (typeof clarity !== 'function' || hasIdentifiedWithClarity) {
      return;
    }

    /**
     * NOTE: 'identify' tells Clarity to associate the provided analytics ID with the current session,
     * which is how we link user behavior back to the known identifier.
     * Pass our internal session ID to Clarity for additional correlation.
     * Note: This is our Joy session ID (Custom Session ID), not Clarity's own session ID.
     */
    try {
      clarity('identify', analyticsId, sessionId);
      hasIdentifiedWithClarity = true;
    } catch (error) {
      addError(error, { context: 'identifyWithClarity#identifyFailed' });
    }
  });
};

/**
 * Sets a custom tag on the current Clarity session.
 * Tags appear as filterable dimensions in the Clarity dashboard.
 *
 * Safe to call before Clarity SDK loads - commands are queued and replayed.
 *
 * @see https://learn.microsoft.com/en-us/clarity/setup-and-installation/cookie-consent#set
 */
export const setClarityTag = (key: string, value: string): void => {
  withWindow(globalWindow => {
    const clarity = globalWindow.clarity;
    if (typeof clarity !== 'function') {
      return;
    }
    try {
      clarity('set', key, value);
    } catch (error) {
      addError(error, { context: 'setClarityTag#setFailed' });
    }
  });
};
